// MACHINERY V2 (Stroje) — V4 shop-floor wall: the kiosk header (M2.14).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.14 + desktop/README.md §V4.
// Rendered truth: desktop/screenshots/v4-wall--refined-claude.png and
// v4-wall--machineid-native.png (both viewed before this file was written, per
// the view-before-code rule). Reference impl: desktop/machinery-v4-wall.jsx
// (V4WallHeader).
//
// Composition, left to right — the artboard's, plus one control it has no
// equivalent for:
//   MACHINE-ID wordmark · divider · teal mono eyebrow + plant name (18/600) ·
//   spacer · zero-padded mono 36/700 status-coloured counters with status icons
//   (wallHeaderCounters.tsx) · divider · live dot + ticking mono clock.
// >>> The exit X (WallExitButton.tsx) is NOT in that row. <<< It is pinned to
// the top-right corner, out of the flow, so the header can go on wrapping
// without carrying it off the corner — see its render site at the bottom.
//
// THE X IS THE ONLY INTERACTIVE THING THE WALL RENDERS. Owner ruling
// 2026-08-24 narrowed the ticket's blanket "NO interactions" to "no
// interactions on the DATA surface": every machine tile is still completely
// inert (WallTile.tsx's header), while this header carries a persistent exit
// "just like we have in the Settings menu". It runs the SAME callback Esc runs
// — see WallExitButton.tsx and FleetWallView.tsx's keydown effect.
//
// SPLIT BY THE SAME RULING. This file stood at 290 of the project's 300 lines,
// so the button could not land beside the counters; the counters moved out to
// wallHeaderCounters.tsx unchanged. The wordmark and divider stayed — they are
// four and thirty lines, and a file of two-line atoms buys nothing.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import WallExitButton from "./WallExitButton";
import WallLiveClock from "./WallLiveClock";
import WallCounters from "./wallHeaderCounters";

const Divider: React.FC = () => (
  <span style={{ height: 22, width: 1, background: "var(--border)", flexShrink: 0 }} />
);

/** Wordmark. Inlined rather than lifted out of ChromeNavV4: that one is mono
 *  14/700 sized for a 52px bar, this one is sans 18/800 for a kiosk header, and
 *  two short blocks beat one helper carrying a size matrix. */
const WallWordmark: React.FC = () => (
  <div
    style={{
      fontWeight: 800,
      fontSize: 18,
      color: "var(--ink)",
      letterSpacing: "0.02em",
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    MACHINE
    <b
      style={{
        background: "var(--ink)",
        color: "var(--card)",
        padding: "3px 6px",
        borderRadius: 5,
        marginLeft: 2,
        fontSize: 14,
      }}
    >
      ID
    </b>
  </div>
);

export interface WallHeaderProps {
  /** Plant / zavod line, derived from the rendered machines' locations. */
  plant: string;
  /** Already-translated "+N more", or null when every plant name is printed.
   *  A SEPARATE field, not appended to `plant`: glued on with the same " + "
   *  separator the names use, a bare numeral read as a site called "3". */
  plantMore: string | null;
  /** countStates() over the machines the grid renders. */
  counts: Record<string, number>;
  /** Poll health for the liveness dot — see WallLiveClock. */
  lastTelemetryOkAt: number;
  /** Leaves the wall. FleetPage's `exitWall`, threaded down unchanged so the X
   *  and Esc are two triggers on ONE implementation. */
  onExit: () => void;
}

const WallHeader: React.FC<WallHeaderProps> = ({
  plant,
  plantMore,
  counts,
  lastTelemetryOkAt,
  onExit,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <header
      style={{
        // THE RIGHT PADDING IS 86, NOT 28, AND IT IS NOT DECORATION. The exit X
        // is pinned out of this flex flow (render site at the bottom of this
        // file), so the 58px it used to occupy in the row — the 28px `gap`
        // before it plus its own 30px — is reserved as padding instead. Flow
        // content therefore stops at exactly the x it stopped at before, at
        // every width, and no wrapped row can run underneath the glyph.
        padding: "18px 86px 18px 28px",
        // Containing block for that pin. Absolute insets resolve against this
        // element's PADDING box, whose right edge coincides with its border
        // box's (there is no right border), so `right: 28` on the X reproduces
        // the 28px inset it had as the last flex child.
        position: "relative",
        borderBottom: "1px solid var(--border)",
        background: "var(--card)",
        display: "flex",
        alignItems: "center",
        // flexWrap + rowGap are OURS, not the artboard's: it is drawn at a fixed
        // 1920 where nothing can collide. Below roughly 1500 the five counters
        // and the clock would otherwise crush the plant name. Wrapping is the
        // graceful degradation; at the 1920 design width nothing wraps. It is
        // KEPT: the X was lifted out of the flow precisely so the counters and
        // the plant name can go on wrapping without dragging it along.
        gap: 28,
        rowGap: 12,
        flexWrap: "wrap",
        flexShrink: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
        <WallWordmark />
        <Divider />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              color: "var(--mod-accent)",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
          >
            {translate("myMachinery:v2.fleet.wall.eyebrow")}
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "var(--ink)",
              marginTop: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {plant}
            {plantMore && (
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: "var(--ink-2)",
                  opacity: 0.7,
                  marginLeft: 8,
                }}
              >
                {plantMore}
              </span>
            )}
          </div>
        </div>
      </div>

      <span style={{ flex: 1, minWidth: 0 }} />

      <WallCounters counts={counts} />

      <Divider />

      {/* The dot + clock are a LEAF (WallLiveClock) for two reasons: the 1 Hz
          tick must not re-render the grid, and the dot is driven by real poll
          health rather than a hardcoded "live". Both in that file's header. */}
      <WallLiveClock lastTelemetryOkAt={lastTelemetryOkAt} />

      {/* THE X IS PINNED, NOT A FLEX CHILD — and that is the whole point.
          As a plain last child it left the corner exactly where the corner
          matters. This header wraps (see the padding comment above), and a
          wrapped line carries no `flex: 1` spacer — the spacer is on line 1 —
          so the X packed to flex-start and sat mid-header, left-aligned. That
          begins around 1500 and covers 1280-1440: the trapped-laptop range this
          rescue affordance exists for. The 1920 hall TV never wraps and never
          showed it. `marginLeft: "auto"` is no fix — it only pushes right
          WITHIN a line and does nothing once the item has wrapped.
          Pinned against the header's padding box instead: `right: 28` is that
          box's own right edge, i.e. where the last flex child sat, and
          `top: 50%` + `translateY(-50%)` centres the 30px box in it. With the
          header's symmetric 18px vertical padding that is the same y
          `alignItems: "center"` produced, so at 1920 this renders exactly as
          before — only now the corner holds at every width.
          No divider before it: the owner asked for the Settings X, and Settings
          puts no rule beside its own — 28px of clearance is separation enough,
          and every pixel of chrome on a hall TV has to earn itself. */}
      <div
        style={{
          position: "absolute",
          right: 28,
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <WallExitButton onExit={onExit} />
      </div>
    </header>
  );
};

export default WallHeader;
